'use client';

import { c as _c } from "react/compiler-runtime";
import { usePostHog } from 'posthog-js/react';
import { useMemo } from 'react';
import { _POSTHOG_SHARED_CONFIG } from '~/modules/posthog/shared';
import type { ActivitySearchMode } from './shared';

/**
 * Event-name registry for AI Search analytics. Keep the source of truth for both
 * the event name AND the payload shape in this one file so renames + discovery
 * are trivial.
 *
 * IMPORTANT: payloads must never include free-text user content (no `prompt`,
 * messages, emails, names). Only structural / numeric signals.
 */
export const AI_SEARCH_EVENTS = {
  MODE_SWITCHED: 'ai_search.mode_switched',
  PROMPT_SUBMITTED: 'ai_search.prompt_submitted',
  RESULTS_SHOWN: 'ai_search.results_shown',
  NO_RESULTS: 'ai_search.no_results',
  EMPTY_STATE_HANDOFF: 'ai_search.empty_state_handoff',
  REFUSAL: 'ai_search.refusal',
  ANSWER_RATED: 'ai_search.answer_rated',
  RESULT_CARD_CLICKED: 'ai_search.result_card_clicked',
  CHAT_RESUMED: 'ai_search.chat_resumed',
  HISTORY_OPENED: 'ai_search.history_opened'
} as const;
export type AiSearchEventName = (typeof AI_SEARCH_EVENTS)[keyof typeof AI_SEARCH_EVENTS];
export type AiSearchRefusalReason = 'safety' | 'off_topic' | 'unknown';
type AiSearchEventPayloads = {
  [AI_SEARCH_EVENTS.MODE_SWITCHED]: {
    to_mode: ActivitySearchMode;
    from_mode: ActivitySearchMode;
  };
  [AI_SEARCH_EVENTS.PROMPT_SUBMITTED]: {
    is_followup: boolean;
    prompt_length: number;
    session_id: string;
  };
  [AI_SEARCH_EVENTS.RESULTS_SHOWN]: {
    // `null` on the "filters" path: the results grid owns the count, so the
    // tool result carries none.
    result_count: number | null;
    model_extraction: string;
    model_ranking: string | null;
    latency_ms: number;
    session_id: string;
  };
  [AI_SEARCH_EVENTS.NO_RESULTS]: {
    prompt_length: number;
    session_id: string;
  };
  [AI_SEARCH_EVENTS.EMPTY_STATE_HANDOFF]: {
    session_id: string | null;
  };
  [AI_SEARCH_EVENTS.REFUSAL]: {
    block_reason: AiSearchRefusalReason;
    session_id: string;
  };
  [AI_SEARCH_EVENTS.ANSWER_RATED]: {
    session_id: string;
    message_id: string;
    rating: AiSearchAnswerRating;
  };
  [AI_SEARCH_EVENTS.RESULT_CARD_CLICKED]: {
    session_id: string;
    activity_id: number;
    position: number;
  };
  [AI_SEARCH_EVENTS.CHAT_RESUMED]: {
    session_id: string;
    source: 'recent_list' | 'history_drawer' | 'hero_popover';
  };
  [AI_SEARCH_EVENTS.HISTORY_OPENED]: {
    session_id: string | null;
    source: 'chat_panel' | 'sidebar' | 'hero';
  };
};

/** Verdict on a single assistant answer. */
export type AiSearchAnswerRating = 'good' | 'bad';
type PosthogLike = {
  capture: (event: string, props?: Record<string, unknown>) => void;
};

/**
 * Thin no-op wrapper. Always go through this so tests / disabled environments
 * never hit the network and consumers never need to null-check.
 */
export function captureAiSearchEvent<E extends AiSearchEventName>(posthog: PosthogLike | null | undefined, event: E, payload: AiSearchEventPayloads[E]): void {
  if (!_POSTHOG_SHARED_CONFIG.ENABLED) return;
  if (!posthog) return;
  posthog.capture(event, payload);
}

/**
 * Capture the answer-level verdict. Emits TWO events:
 *
 * 1. A structured `ai_search.answer_rated` event for funnel/quality analytics.
 * 2. PostHog's native `$ai_feedback` event, linked to the conversation via
 *    `$ai_session_id` (the chat route tags every generation with the same
 *    `$ai_session_id: chatId`). This surfaces the rating inside PostHog LLM
 *    observability under the session that produced the answer.
 *
 * No free text is collected: the control is a two-way verdict, so both events
 * carry only slugs and ids.
 */
export function captureAnswerRating(posthog: PosthogLike | null | undefined, args: {
  chatId: string;
  /** Assistant message being rated. */
  messageId: string;
  /** Absent on messages streamed before the route stamped it; the rating then
   *  falls back to session-level attribution rather than being dropped. */
  traceId?: string;
  rating: AiSearchAnswerRating;
}): void {
  if (!_POSTHOG_SHARED_CONFIG.ENABLED) return;
  if (!posthog) return;
  captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.ANSWER_RATED, {
    session_id: args.chatId,
    message_id: args.messageId,
    rating: args.rating
  });
  posthog.capture('$ai_feedback', {
    $ai_session_id: args.chatId,
    ...(args.traceId !== undefined && {
      $ai_trace_id: args.traceId
    }),
    $ai_feedback_rating: args.rating === 'good' ? 1 : 0,
    message_id: args.messageId
  });
}

// `ANSWER_RATED` goes through `captureAnswerRating`, so it is not in the hook map.
export type AiSearchAnalytics = { [K in Exclude<keyof AiSearchEventPayloads, typeof AI_SEARCH_EVENTS.ANSWER_RATED>]: (payload: AiSearchEventPayloads[K]) => void };

/**
 * Hook returning typed `track*` functions. Internally calls `posthog.capture()`
 * fire-and-forget. No-ops when posthog is unavailable or disabled.
 */
export function useAiSearchAnalytics() {
  const $ = _c(28);
  const posthog = usePostHog();
  let t0;
  if ($[0] !== posthog) {
    t0 = payload => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.MODE_SWITCHED, payload);
    $[0] = posthog;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== posthog) {
    t1 = payload_0 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.PROMPT_SUBMITTED, payload_0);
    $[2] = posthog;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== posthog) {
    t2 = payload_1 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.RESULTS_SHOWN, payload_1);
    $[4] = posthog;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== posthog) {
    t3 = payload_2 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.NO_RESULTS, payload_2);
    $[6] = posthog;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== posthog) {
    t4 = payload_3 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.EMPTY_STATE_HANDOFF, payload_3);
    $[8] = posthog;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== posthog) {
    t5 = payload_4 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.REFUSAL, payload_4);
    $[10] = posthog;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== posthog) {
    t6 = payload_5 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.RESULT_CARD_CLICKED, payload_5);
    $[12] = posthog;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== posthog) {
    t7 = payload_6 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.CHAT_RESUMED, payload_6);
    $[14] = posthog;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== posthog) {
    t8 = payload_7 => captureAiSearchEvent(posthog, AI_SEARCH_EVENTS.HISTORY_OPENED, payload_7);
    $[16] = posthog;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t0 || $[19] !== t1 || $[20] !== t2 || $[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8) {
    t9 = {
      [AI_SEARCH_EVENTS.MODE_SWITCHED]: t0,
      [AI_SEARCH_EVENTS.PROMPT_SUBMITTED]: t1,
      [AI_SEARCH_EVENTS.RESULTS_SHOWN]: t2,
      [AI_SEARCH_EVENTS.NO_RESULTS]: t3,
      [AI_SEARCH_EVENTS.EMPTY_STATE_HANDOFF]: t4,
      [AI_SEARCH_EVENTS.REFUSAL]: t5,
      [AI_SEARCH_EVENTS.RESULT_CARD_CLICKED]: t6,
      [AI_SEARCH_EVENTS.CHAT_RESUMED]: t7,
      [AI_SEARCH_EVENTS.HISTORY_OPENED]: t8
    };
    $[18] = t0;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}